import { Registerable } from '@baserow/modules/core/registry'

export class WorkspaceSettingsPageType extends Registerable {
  /**
   * The name of the page in the tabs navigation at the top of the page.
   */
  getName() {
    return null
  }

  constructor(...args) {
    super(...args)
    this.type = this.getType()

    if (this.type === null) {
      throw new Error('The type name of a workspace settings page must be set.')
    }
  }

  serialize() {
    return {
      type: this.type,
      name: this.getName(),
    }
  }

  /**
   * Responsible for returning whether the user has access
   * to this workspace settings page type. By default, they do.
   */
  hasPermission(workspace) {
    return true
  }

  /**
   * Optional stable NavCapability id (e.g. nav.members). When set, $canNavCapability must allow read.
   */
  getNavCapabilityId() {
    return null
  }

  hasNavCapability(workspace) {
    const id = this.getNavCapabilityId()
    if (!id) {
      return true
    }
    return this.app.$canNavCapability(id, workspace.id)
  }

  /**
   * Responsible for returning whether the user has access to
   * this page's features in this workspace.
   */
  isFeatureActive(workspace) {
    return true
  }

  getFeatureDeactivatedModal(workspace) {
    return null
  }

  getRoute() {
    throw new Error('The `getRoute` method must be set.')
  }
}

export class MembersWorkspaceSettingsPageType extends WorkspaceSettingsPageType {
  static getType() {
    return 'members'
  }

  getName() {
    const { $i18n: i18n } = this.app
    return i18n.t('membersSettings.membersTabTitle')
  }

  getRoute(workspace) {
    return {
      name: 'settings-members',
      params: {
        workspaceId: workspace.id,
      },
    }
  }

  getNavCapabilityId() {
    return 'nav.members'
  }
}

export class InvitesWorkspaceSettingsPageType extends WorkspaceSettingsPageType {
  static getType() {
    return 'invites'
  }

  getName() {
    const { $i18n: i18n } = this.app
    return i18n.t('membersSettings.invitesTabTitle')
  }

  /**
   * Responsible for returning whether the user has access to the
   * invitations table by checking their `workspace.list_invitations` permission.
   */
  hasPermission(workspace) {
    return this.app.$hasPermission(
      'workspace.list_invitations',
      workspace,
      workspace.id
    )
  }

  getRoute(workspace) {
    return {
      name: 'settings-invites',
      params: {
        workspaceId: workspace.id,
      },
    }
  }

  getNavCapabilityId() {
    return 'nav.invite'
  }
}

export class RolesAndPermissionsWorkspaceSettingsPageType extends WorkspaceSettingsPageType {
  static getType() {
    return 'roles-and-permissions'
  }

  getName() {
    const { $i18n: i18n } = this.app
    return i18n.t('rolesAndPermissionsSettings.title')
  }

  /**
   * Only workspace admins can access roles and permissions management.
   */
  hasPermission(workspace) {
    return this.app.$hasPermission(
      'workspace.manage',
      workspace,
      workspace.id
    )
  }

  getRoute(workspace) {
    return {
      name: 'settings-roles-and-permissions',
      params: {
        workspaceId: workspace.id,
      },
    }
  }

  getNavCapabilityId() {
    return 'nav.settings_roles'
  }
}

export class AgentsWorkspaceSettingsPageType extends WorkspaceSettingsPageType {
  static getType() {
    return 'agents'
  }

  getName() {
    const { $i18n: i18n } = this.app
    return i18n.t('agentsSettings.tabTitle')
  }

  /**
   * Same visibility as Members: users who can list workspace users can open Agentes.
   */
  hasPermission(workspace) {
    return (
      this.app.$hasPermission(
        'workspace.list_workspace_users',
        workspace,
        workspace.id
      )
    )
  }

  getRoute(workspace) {
    return {
      name: 'settings-agents',
      params: {
        workspaceId: workspace.id,
      },
    }
  }

  getNavCapabilityId() {
    return 'nav.agents'
  }
}

export class SolutionKitsWorkspaceSettingsPageType extends WorkspaceSettingsPageType {
  static getType() {
    return 'solution-kits'
  }

  getName() {
    const { $i18n: i18n } = this.app
    return i18n.t('solutionKits.settingsTitle')
  }

  /**
   * Same visibility as Members/Agents: users who can list workspace users can open Solution Kits.
   */
  hasPermission(workspace) {
    return this.app.$hasPermission(
      'workspace.list_workspace_users',
      workspace,
      workspace.id
    )
  }

  getRoute(workspace) {
    return {
      name: 'settings-kits',
      params: {
        workspaceId: workspace.id,
      },
    }
  }

  getNavCapabilityId() {
    return 'nav.solution_kits'
  }
}
